import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCompanyEmailHistory, WidgetEmailLog } from '../companyHelpers';
import style from './CompanyEmailHistoryModal.module.css';

interface CompanyEmailHistoryModalProps {
  /** §FB37: вкладка сторінки компанії — без оверлею та кнопки закриття. */
  embedded?: boolean;
  companyId: number;
  companyName?: string;
  onClose: () => void;
}

// Дата/час надсилання у київському поясі: «08.07.2026, 19:14».
const formatSentAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('uk-UA', {
      timeZone: 'Europe/Kyiv',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('uk-UA');
  }
};

// Тип листа → людяна назва + клас пігулки.
const kindLabel = (k?: string): string => {
  if (k === 'access') return 'Доступ';
  if (k === 'widget') return 'QR для віджету';
  return k || 'Лист';
};

const MailGlyph = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const CompanyEmailHistoryModal: React.FC<CompanyEmailHistoryModalProps> = ({
  companyId,
  companyName,
  onClose,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<WidgetEmailLog[] | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(false);
    getCompanyEmailHistory(companyId)
      .then((data) => {
        if (active) setRows(data);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  return (
    <div
      className={embedded ? style.embeddedWrap : style.modalOverlay}
      onClick={embedded ? undefined : onClose}
    >
      <div
        className={`${style.modal} ${embedded ? style.modalEmbedded : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!embedded && (
          <div className={style.header}>
            <h5 className={style.title}>Історія надсилання листів</h5>
            {!embedded && (
              <button
                className={style.closeButton}
                onClick={onClose}
                aria-label="Закрити"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className={style.body}>
          {companyName && <p className={style.company}>{companyName}</p>}
          <p className={style.subhint}>
            Усі надіслані листи (доступ, QR для віджету) — від нових до старих.{' '}
            {/* §MAIL: стан доставки кожного листа компанії — у журналі пошти. */}
            <button
              type="button"
              className={style.journalLink}
              onClick={() => navigate(`/mail?companyId=${companyId}`)}
            >
              Стан доставки — у журналі пошти →
            </button>
          </p>

          {rows === null && !error && (
            <p className={style.muted}>Завантаження…</p>
          )}
          {error && (
            <p className={style.errorText}>
              Не вдалося завантажити історію. Спробуйте ще раз.
            </p>
          )}

          {rows !== null && !error && rows.length === 0 && (
            <div className={style.emptyState}>
              <span className={style.emptyIcon}>
                <MailGlyph />
              </span>
              <p className={style.emptyTitle}>Листів ще не надсилали</p>
              <p className={style.emptyNote}>
                Тут з’являться всі листи, надіслані цій компанії: запрошення
                (доступ) та QR для віджету — із датою, отримувачем і
                відправником.
              </p>
            </div>
          )}

          {rows !== null && !error && rows.length > 0 && (
            <div className={style.list}>
              <div className={`${style.row} ${style.headRow}`}>
                <span className={style.colDate}>Дата й час</span>
                <span className={style.colType}>Тип</span>
                <span className={style.colEmail}>Кому (email)</span>
                <span className={style.colBy}>Хто надіслав</span>
              </div>
              {rows.map((h, i) => (
                <div className={style.row} key={i}>
                  <span className={style.colDate}>
                    {formatSentAt(h.sentAt)}
                  </span>
                  <span className={style.colType}>
                    <span
                      className={`${style.badge} ${
                        h.kind === 'access'
                          ? style.badgeAccess
                          : style.badgeWidget
                      }`}
                    >
                      {kindLabel(h.kind)}
                    </span>
                  </span>
                  <span className={style.colEmail} title={h.recipientEmail}>
                    {h.recipientEmail}
                  </span>
                  <span className={style.colBy} title={h.sentBy || ''}>
                    {h.sentBy || '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyEmailHistoryModal;
